/**
 * ChatChannelDropdown Component
 *
 * Dropdown menu for selecting channels and DMs in a compact header format.
 * Replaces the sidebar layout to maximize message area width.
 *
 * @module components/chat/ChatChannelDropdown
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Hash,
  MessageCircle,
  ChevronDown,
  Users,
  Lock,
  Globe,
  Folder,
  Bot,
  User,
  Wifi,
  WifiOff,
  Circle,
  Plus,
  X,
  Pin,
  PinOff
} from 'lucide-react';
import { useChat, CONNECTION_STATE } from '../../contexts/ChatContext.jsx';
import UnreadBadge from './UnreadBadge.jsx';

// ============================================
// Constants
// ============================================

const CHANNEL_TYPE_ICONS = {
  public: Globe,
  project: Folder,
  role_restricted: Lock
};

const AGENT_STATUS_COLORS = {
  ready: 'bg-green-500',
  working: 'bg-yellow-500',
  focus: 'bg-red-500',
  in_call: 'bg-purple-500',
  offline: 'bg-gray-500'
};

// ============================================
// Sub-Components
// ============================================

/**
 * Connection status indicator (compact)
 */
function ConnectionIndicator({ state }) {
  const isConnected = state === CONNECTION_STATE.CONNECTED;
  const isReconnecting = state === CONNECTION_STATE.RECONNECTING;
  const isConnecting = state === CONNECTION_STATE.CONNECTING;

  if (isConnected) {
    return <Wifi className="w-4 h-4 text-green-500" title="Connected" />;
  }
  if (isReconnecting || isConnecting) {
    return <Wifi className="w-4 h-4 text-yellow-500 animate-pulse" title={isReconnecting ? 'Reconnecting...' : 'Connecting...'} />;
  }
  return <WifiOff className="w-4 h-4 text-red-500" title="Disconnected" />;
}

/**
 * Agent status indicator
 */
function AgentStatusIndicator({ status }) {
  const statusColor = AGENT_STATUS_COLORS[status] || AGENT_STATUS_COLORS.offline;
  return <span className={`w-2 h-2 rounded-full ${statusColor} block`} />;
}

/**
 * Channel item in dropdown
 */
function DropdownChannelItem({ channel, isActive, unreadCount, onClick }) {
  const ChannelTypeIcon = CHANNEL_TYPE_ICONS[channel.type] || Hash;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        w-full flex items-center gap-2 px-3 py-2 text-left transition-colors
        ${isActive
          ? 'bg-indigo-600/20 text-indigo-400'
          : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
        }
      `}
    >
      <ChannelTypeIcon className="w-4 h-4 flex-shrink-0 text-gray-400" />
      <span className="flex-1 truncate text-sm">
        {channel.displayName || channel.name}
      </span>
      <UnreadBadge count={unreadCount} />
    </button>
  );
}

/**
 * DM item in dropdown
 */
function DropdownDmItem({ recipient, isActive, unreadCount, status, onClick }) {
  const isAgent = recipient.type === 'agent';
  const RecipientIcon = isAgent ? Bot : User;
  // For agents, always show status (default to offline if unknown)
  const displayStatus = isAgent ? (status || 'offline') : null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        w-full flex items-center gap-2 px-3 py-2 text-left transition-colors
        ${isActive
          ? 'bg-indigo-600/20 text-indigo-400'
          : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
        }
      `}
    >
      <RecipientIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
      <span className="flex-1 truncate text-sm">
        {recipient.displayName || recipient.name}
      </span>
      {displayStatus && (
        <AgentStatusIndicator status={displayStatus} />
      )}
      <UnreadBadge count={unreadCount} />
    </button>
  );
}

// ============================================
// Main Component
// ============================================

/**
 * ChatChannelDropdown Component
 *
 * Compact dropdown for channel/DM selection in header.
 *
 * @param {object} props - Component props
 * @param {Function} [props.onCreateChannel] - Callback to create a new channel
 * @param {Function} [props.onClose] - Callback to close the chat panel
 * @param {boolean} [props.isPinned] - Whether the panel is pinned (docked mode)
 * @param {Function} [props.onTogglePin] - Callback to toggle pinned state
 * @param {string} [props.className] - Additional CSS classes
 * @returns {React.ReactElement} Channel dropdown header
 */
export default function ChatChannelDropdown({
  onCreateChannel,
  onClose,
  isPinned = false,
  onTogglePin,
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const {
    channels,
    channelsLoading,
    activeChannel,
    activeDmRecipient,
    connectionState,
    unreadCounts,
    dmUnreadCounts,
    getUnreadCount,
    getDmUnreadCount,
    getAgentStatus,
    isAgentGenerating,
    selectChannel,
    selectDmRecipient,
    dmContacts,
    totalUnreadCount,
    fetchChannelMembers,
    getChannelMembers
  } = useChat();

  // Build DM recipients list
  const dmRecipientsList = useMemo(() => {
    if (!dmContacts) return [];

    const recipientMap = new Map();

    for (const contact of dmContacts.recent || []) {
      recipientMap.set(contact.id, contact);
    }

    for (const agent of dmContacts.agents || []) {
      if (!recipientMap.has(agent.id)) {
        recipientMap.set(agent.id, agent);
      }
    }

    for (const user of dmContacts.users || []) {
      if (!recipientMap.has(user.id)) {
        recipientMap.set(user.id, user);
      }
    }

    return Array.from(recipientMap.values());
  }, [dmContacts]);

  // Current selection display
  const currentSelection = useMemo(() => {
    if (activeChannel) {
      const ChannelTypeIcon = CHANNEL_TYPE_ICONS[activeChannel.type] || Hash;
      return {
        icon: ChannelTypeIcon,
        name: activeChannel.displayName || activeChannel.name,
        type: 'channel'
      };
    }
    if (activeDmRecipient) {
      const isAgent = activeDmRecipient.type === 'agent';
      return {
        icon: isAgent ? Bot : User,
        name: activeDmRecipient.displayName || activeDmRecipient.name,
        type: 'dm',
        isAgent
      };
    }
    return {
      icon: MessageCircle,
      name: 'Select conversation',
      type: null
    };
  }, [activeChannel, activeDmRecipient]);

  // Fetch channel members when active channel changes
  useEffect(() => {
    console.log('[ChatChannelDropdown] activeChannel changed:', activeChannel?.id);
    if (activeChannel?.id) {
      console.log('[ChatChannelDropdown] Fetching members for channel:', activeChannel.id);
      fetchChannelMembers(activeChannel.id);
    }
  }, [activeChannel?.id, fetchChannelMembers]);

  // Get current channel members
  const currentMembers = useMemo(() => {
    if (!activeChannel?.id) return null;
    const members = getChannelMembers(activeChannel.id);
    console.log('[ChatChannelDropdown] currentMembers for', activeChannel.id, ':', members);
    return members;
  }, [activeChannel?.id, getChannelMembers]);

  // Debug: Log on every render
  console.log('[ChatChannelDropdown] RENDER - activeChannel:', activeChannel?.id, 'currentMembers:', currentMembers);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle channel selection
  const handleSelectChannel = useCallback((channel) => {
    selectChannel(channel);
    setIsOpen(false);
  }, [selectChannel]);

  // Handle DM selection
  const handleSelectDm = useCallback((recipient) => {
    selectDmRecipient(recipient);
    setIsOpen(false);
  }, [selectDmRecipient]);

  const SelectionIcon = currentSelection.icon;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Header Bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700">
        {/* Left: Connection status */}
        <div className="flex items-center gap-2">
          <ConnectionIndicator state={connectionState} />
        </div>

        {/* Center: Dropdown trigger */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-700/50 hover:bg-gray-700 transition-colors"
        >
          <SelectionIcon className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-white font-medium truncate max-w-[200px]">
            {currentSelection.name}
          </span>
          {totalUnreadCount > 0 && !isOpen && (
            <span className="flex items-center justify-center min-w-[20px] h-5 text-xs font-bold text-white bg-red-500 rounded-full px-1">
              {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Right: Pin and Close buttons */}
        <div className="flex items-center gap-1">
          {onTogglePin && (
            <button
              type="button"
              onClick={onTogglePin}
              className={`
                p-1.5 rounded transition-colors
                ${isPinned
                  ? 'bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/30'
                  : 'hover:bg-gray-700/50 text-gray-400 hover:text-white'
                }
              `}
              title={isPinned ? 'Unpin (overlay mode)' : 'Pin (dock mode)'}
            >
              {isPinned ? <Pin className="w-4 h-4" /> : <PinOff className="w-4 h-4" />}
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded hover:bg-gray-700/50 text-gray-400 hover:text-white transition-colors"
              title="Close chat panel"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Channel Members Bar - shown when a channel is active */}
      {activeChannel && currentMembers && (
        <div className="flex items-start gap-2 px-3 py-1.5 bg-gray-800/50 border-b border-gray-700/50">
          <Users className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
          <div className="flex flex-wrap items-center gap-1.5">
            {/* Agents with status dots and generating indicator */}
            {(currentMembers.agents || []).map((agent) => {
              // Prefer live status from WebSocket, fall back to API response
              const status = getAgentStatus(agent.id)?.operationalStatus || agent.operationalStatus || 'offline';
              const statusColor = AGENT_STATUS_COLORS[status] || AGENT_STATUS_COLORS.offline;
              const isGenerating = isAgentGenerating(agent.id);
              return (
                <div
                  key={agent.id}
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-700/30"
                  title={`${agent.displayName || agent.name} - ${status}${isGenerating ? ' (generating)' : ''}`}
                >
                  <Bot className="w-3 h-3 text-gray-400" />
                  <span className="text-xs text-gray-300 whitespace-nowrap">
                    {agent.displayName || agent.name}
                  </span>
                  {isGenerating ? (
                    <span className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" title="Generating..." />
                  ) : (
                    <span className={`w-2 h-2 rounded-full ${statusColor}`} />
                  )}
                </div>
              );
            })}
            {/* Users */}
            {(currentMembers.users || []).map((user) => (
              <div
                key={user.id}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-700/30"
                title={user.displayName || user.name}
              >
                <User className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-gray-300 whitespace-nowrap">
                  {user.displayName || user.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-50 bg-gray-800 border-b border-gray-700 shadow-lg max-h-[400px] overflow-y-auto">
          {/* Channels Section */}
          <div className="border-b border-gray-700/50">
            <div className="flex items-center justify-between px-3 py-2 bg-gray-900/50">
              <div className="flex items-center gap-2">
                <Hash className="w-4 h-4 text-gray-400" />
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Channels
                </span>
                <span className="text-xs text-gray-500">({channels.length})</span>
              </div>
              {onCreateChannel && (
                <button
                  type="button"
                  onClick={() => {
                    onCreateChannel();
                    setIsOpen(false);
                  }}
                  className="p-1 rounded hover:bg-gray-700/50 text-gray-400 hover:text-gray-200 transition-colors"
                  title="Create channel"
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
            </div>

            {channelsLoading ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                Loading...
              </div>
            ) : channels.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                No channels available
              </div>
            ) : (
              <div>
                {channels.map((channel) => (
                  <DropdownChannelItem
                    key={channel.id}
                    channel={channel}
                    isActive={activeChannel?.id === channel.id}
                    unreadCount={getUnreadCount(channel.id)}
                    onClick={() => handleSelectChannel(channel)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Direct Messages Section */}
          <div>
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-900/50">
              <Users className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Direct Messages
              </span>
              <span className="text-xs text-gray-500">({dmRecipientsList.length})</span>
            </div>

            {dmRecipientsList.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                No direct messages
              </div>
            ) : (
              <div>
                {dmRecipientsList.map((recipient) => {
                  // For agents: prefer live WebSocket status, fall back to API response
                  const agentStatus = recipient.type === 'agent'
                    ? (getAgentStatus(recipient.id)?.operationalStatus || recipient.operationalStatus)
                    : null;

                  return (
                    <DropdownDmItem
                      key={recipient.id}
                      recipient={recipient}
                      isActive={activeDmRecipient?.id === recipient.id}
                      unreadCount={getDmUnreadCount(recipient.id)}
                      status={agentStatus}
                      onClick={() => handleSelectDm(recipient)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export {
  ConnectionIndicator,
  AgentStatusIndicator,
  DropdownChannelItem,
  DropdownDmItem
};
